import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, LogIn, Sparkles, User, Plus, ChevronLeft } from "lucide-react";
import { PasswordResetFlow } from "@/components/game/PasswordReset";

// Floating fruit background
const FLOAT_FRUITS = ['🍎','🍊','🍋','🍇','🍓','🍌','🍑','🍒','🥭','🍍','🫐','🥝'];

function FloatingFruits() {
  const fruits = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    emoji: FLOAT_FRUITS[i % FLOAT_FRUITS.length],
    left: `${(i * 5.8) % 96}%`,
    size: `${20 + (i % 4) * 8}px`,
    delay: `${i * 0.7}s`,
    duration: `${8 + (i % 5) * 2}s`,
    opacity: 0.15 + (i % 3) * 0.05
  })), []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {fruits.map((f, i) => (
        <div
          key={i}
          className="absolute animate-float-drift"
          style={{
            left: f.left,
            bottom: '-40px',
            fontSize: f.size,
            animationDelay: f.delay,
            animationDuration: f.duration,
            opacity: f.opacity
          }}
        >
          {f.emoji}
        </div>
      ))}
      <style>{`
        @keyframes float-drift {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-110vh) rotate(360deg); opacity: 0; }
        }
        .animate-float-drift { animation: float-drift linear infinite; }
      `}</style>
    </div>
  );
}

// Profile Selection Component
function ProfileSelection({ profiles, email, password, onSelectProfile, onCreateProfile, onBack }) {
  const [isCreating, setIsCreating] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSelectProfile = async (profileId) => {
    setIsLoading(true);
    await onSelectProfile(profileId);
    setIsLoading(false);
  };

  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) {
      toast.error("Please enter a profile name");
      return;
    }
    setIsLoading(true);
    const success = await onCreateProfile(newProfileName.trim());
    if (!success) {
      setIsLoading(false);
    }
  };

  if (isCreating) {
    return (
      <Card className="w-full max-w-md shadow-playful border-2 border-border">
        <CardHeader className="text-center space-y-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute left-4 top-4"
            onClick={() => setIsCreating(false)}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <CardTitle className="font-display text-2xl">Create New Profile</CardTitle>
          <CardDescription>Choose a name for your new profile</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profileName">Profile Name</Label>
            <Input
              id="profileName"
              placeholder="Enter profile name..."
              value={newProfileName}
              onChange={(e) => setNewProfileName(e.target.value)}
              maxLength={20}
            />
          </div>
          <Button 
            className="w-full" 
            onClick={handleCreateProfile}
            disabled={isLoading}
          >
            {isLoading ? "Creating..." : "Create Profile"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md shadow-playful border-2 border-border">
      <CardHeader className="text-center space-y-2">
        <Button 
          variant="ghost" 
          size="sm" 
          className="absolute left-4 top-4"
          onClick={onBack}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex justify-center mb-2">
          <span className="text-4xl">👤</span>
        </div>
        <CardTitle className="font-display text-2xl">Select Profile</CardTitle>
        <CardDescription>Choose a profile to continue</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {profiles.map((profile) => (
          <Button
            key={profile.id}
            variant="outline"
            className="w-full h-auto py-4 flex items-center justify-between hover:bg-primary/10 transition-colors"
            onClick={() => handleSelectProfile(profile.id)}
            disabled={isLoading}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-lg">{profile.player_name || "Player"}</div>
                <div className="text-sm text-muted-foreground">Level {profile.max_level || 1}</div>
              </div>
            </div>
            <div className="text-2xl">▶️</div>
          </Button>
        ))}
        
        {profiles.length < 5 && (
          <Button
            variant="ghost"
            className="w-full h-auto py-4 border-2 border-dashed border-muted-foreground/30 hover:border-primary/50"
            onClick={() => setIsCreating(true)}
            disabled={isLoading}
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Profile
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showProfileSelection, setShowProfileSelection] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [loginCredentials, setLoginCredentials] = useState({ email: "", password: "" });
  const { login, selectProfile, createProfile } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      toast.success("Welcome back!");
      navigate("/map");
    } else if (result.requiresProfileSelection) {
      // Multiple profiles exist - show profile selection
      setProfiles(result.profiles);
      setLoginCredentials({ email: result.email, password: result.password });
      setShowProfileSelection(true);
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  const handleSelectProfile = async (profileId) => {
    const result = await selectProfile(loginCredentials.email, loginCredentials.password, profileId);
    if (result.success) {
      toast.success("Welcome back!");
      navigate("/map");
    } else {
      toast.error(result.error);
    }
  };

  const handleCreateProfile = async (playerName) => {
    const result = await createProfile(loginCredentials.email, loginCredentials.password, playerName);
    if (result.success) {
      toast.success("Profile created! Welcome!");
      navigate("/map");
      return true;
    } else {
      toast.error(result.error);
      return false;
    }
  };

  // Show password reset flow
  if (showPasswordReset) {
    return (
      <div className="min-h-screen game-gradient-bg pattern-dots flex items-center justify-center p-4">
        <PasswordResetFlow 
          onBack={() => setShowPasswordReset(false)}
          onSuccess={() => {
            setShowPasswordReset(false);
            toast.success("Password reset! Please sign in.");
          }}
        />
      </div>
    );
  }

  // Show profile selection
  if (showProfileSelection) {
    return (
      <div className="min-h-screen game-gradient-bg pattern-dots flex items-center justify-center p-4">
        <ProfileSelection
          profiles={profiles}
          email={loginCredentials.email}
          password={loginCredentials.password}
          onSelectProfile={handleSelectProfile}
          onCreateProfile={handleCreateProfile}
          onBack={() => {
            setShowProfileSelection(false);
            setProfiles([]);
            setLoginCredentials({ email: "", password: "" });
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <FloatingFruits />
      
      {/* Radial glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[100px] pointer-events-none" />
      
      <Card className="w-full max-w-md shadow-2xl border border-white/10 bg-slate-900/80 backdrop-blur-xl relative z-10">
        <CardHeader className="text-center space-y-2">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <div className="relative">
              <span className="text-6xl drop-shadow-lg" style={{ filter: 'drop-shadow(0 4px 12px rgba(255,200,0,0.4))' }}>🍌</span>
              <Sparkles className="absolute -top-1 -right-3 w-6 h-6 text-yellow-400 animate-pulse" />
            </div>
          </div>
          
          <CardTitle className="font-display text-3xl font-bold tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400">Fruit Crush</span>
          </CardTitle>
          
          <CardDescription className="font-body text-slate-400">
            Sign in to continue your fruity adventure!
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="font-body font-medium text-slate-300">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 font-body bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-400"
                  disabled={isLoading}
                  data-testid="login-email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="font-body font-medium text-slate-300">
                  Password
                </Label>
                <button
                  type="button"
                  onClick={() => setShowPasswordReset(true)}
                  className="text-xs text-orange-400 hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 font-body bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-400"
                  disabled={isLoading}
                  data-testid="login-password"
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              size="lg"
              className="w-full font-display font-semibold text-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg shadow-orange-500/25 text-white border-0"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? (
                <span className="animate-pulse">Signing in...</span>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="font-body text-slate-400">
              New to Fruit Crush?{" "}
              <Link 
                to="/register" 
                className="font-semibold text-orange-400 hover:underline"
              >
                Create an account
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
